import type { ReactNode } from 'react'
import { useLocation } from 'react-router-dom'
import { useAuth } from '../lib/auth'

const ADMIN_NAV: [string, string, string][] = [
  ['/admin', 'Tableau de bord', '▦'],
  ['/admin/clients', 'Clients', '👥'],
  ['/admin/projets', 'Projets', '🎬'],
  ['/admin/rencontres', 'Rencontres', '📅'],
  ['/admin/comptabilite', 'Comptabilité & factures', '🧾'],
  ['/admin/livrables', 'Livrables & revue', '🎬'],
  ['/admin/fichiers', 'Fichiers', '📁'],
  ['/admin/portfolio', 'Portfolio', '🎞'],
  ['/admin/leads', 'CRM / Leads', '📊'],
  ['/outils-ia', 'Outils IA', '🤖'],
]
const CLIENT_NAV: [string, string, string][] = [
  ['/portail', 'Mon espace', '▦'],
  ['/mot-de-passe', 'Mot de passe', '🔑'],
]

export default function Layout({ children, title, actions, wide }: { children: ReactNode; title?: string; actions?: ReactNode; wide?: boolean }) {
  const { profile, signOut, isAdmin } = useAuth()
  const { pathname } = useLocation()
  const nav = isAdmin ? ADMIN_NAV : CLIENT_NAV
  const active = (href: string) => href === '/admin' ? pathname === '/admin' : pathname.startsWith(href)

  return (
    <div className="min-h-screen bg-noir text-blanc">
      <header className="flex items-center justify-between px-5 md:px-8 py-4 border-b border-white/10 sticky top-0 bg-noir/95 backdrop-blur z-20">
        <a href={isAdmin ? '/admin' : '/portail'} className="flex items-center gap-2 font-display tracking-widest">
          <span className="text-orange text-2xl">[</span>
          <span className="text-blanc text-xl tracking-[0.25em]">PIVOT</span>
          <span className="text-orange text-2xl">]</span>
          <span className="text-acier text-xs tracking-[0.4em] ml-1">MÉDIA</span>
        </a>
        <nav className="flex items-center gap-5 text-xs uppercase tracking-widest text-acier">
          <a href="/" className="hover:text-orange hidden sm:inline">Site</a>
          <span className="text-blanc hidden sm:inline">{profile?.full_name || profile?.email}</span>
          <button onClick={signOut} className="hover:text-orange">Déconnexion</button>
        </nav>
      </header>

      <div className="md:flex">
        <aside className="md:w-56 md:shrink-0 md:border-r border-b md:border-b-0 border-white/10 md:sticky md:top-[65px] md:h-[calc(100vh-65px)] overflow-x-auto md:overflow-y-auto bg-noir">
          <nav className="flex md:flex-col gap-1 p-2 md:p-3 min-w-max md:min-w-0">
            {nav.map(([href, label, icon]) => (
              <a key={href} href={href}
                className={`flex items-center gap-3 px-3 py-2 rounded text-xs uppercase tracking-widest whitespace-nowrap transition-colors ${active(href) ? 'bg-orange/15 text-orange' : 'text-acier hover:text-blanc hover:bg-white/5'}`}>
                <span className="w-5 text-center text-sm">{icon}</span>{label}
              </a>
            ))}
          </nav>
        </aside>

        <main className={`flex-1 min-w-0 px-5 md:px-8 py-8 ${wide ? '' : 'max-w-6xl'}`}>
          {(title || actions) && (
            <div className="flex flex-wrap items-center justify-between gap-3 mb-8">
              {title && <h1 className="font-display text-4xl tracking-wide">{title}</h1>}
              {actions && <div className="flex flex-wrap gap-2">{actions}</div>}
            </div>
          )}
          {children}
        </main>
      </div>
    </div>
  )
}

export function StatusBadge({ status }: { status: string | null }) {
  const map: Record<string, string> = {
    'planifié': 'bg-blue-500/15 text-blue-300', 'tournage': 'bg-orange/15 text-orange',
    'montage': 'bg-purple-500/15 text-purple-300', 'révision': 'bg-yellow-500/15 text-yellow-300',
    'livré': 'bg-green-500/15 text-green-300', 'annulé': 'bg-red-500/15 text-red-300', 'annulée': 'bg-red-500/15 text-red-300',
    'terminé': 'bg-green-500/15 text-green-300',
    'approved': 'bg-green-500/15 text-green-300', 'pending': 'bg-yellow-500/15 text-yellow-300',
    'brouillon': 'bg-white/10 text-acier', 'envoyée': 'bg-blue-500/15 text-blue-300', 'envoyé': 'bg-blue-500/15 text-blue-300', 'vu': 'bg-green-500/15 text-green-300',
    'payée': 'bg-green-500/15 text-green-300', 'en retard': 'bg-red-500/15 text-red-300',
    'en_attente': 'bg-yellow-500/15 text-yellow-300', 'en_revision': 'bg-blue-500/15 text-blue-300',
    'approuve': 'bg-green-500/15 text-green-300', 'change_demande': 'bg-red-500/15 text-red-300',
  }
  const labels: Record<string, string> = { en_attente: 'En attente', en_revision: 'En révision', approuve: 'Approuvée', change_demande: 'Changements demandés' }
  const cls = map[status || ''] || 'bg-white/10 text-acier'
  return <span className={`text-[10px] uppercase tracking-wider px-2 py-1 rounded whitespace-nowrap ${cls}`}>{labels[status || ''] || status || '—'}</span>
}
